import {useRouter} from '@tanstack/react-router';
import {createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState} from 'react';

import {useImagesIds} from '~api/images.queries';
import {useLabelInstancesIds} from '~api/label-instances.queries';
import {Route} from '~utils/routeUtil';
import {selectCurrentModel} from '~redux/reducers/annotatorReducer';
import {selectImages} from '~redux/reducers/imageReducer';
import {useAppSelector} from '~redux/index';

import {useImageFilters} from '~components/images/image-overview/imageOverview.hooks';
import {ImageOverviewMode} from '~components/images/utils/image-mode-mixins';
import {useLabelItems} from '~components/models/model.hooks';
import {useCheckedItems} from './checkedItems.hooks';

interface CombinedCheckedItemsContextValue {
  imageCheckedItemsState: CheckedItemsState;
  labelInstancesCheckedItemsState: CheckedItemsState;
  isLoadingImageIds: boolean;
  isLoadingLabelInstanceIds: boolean;
  imageIds?: string[];
  labelInstanceIds?: string[];
  loadImageIds: () => void;
  loadLabelInstanceIds: () => void;
  setCurrentPageImageIds: (ids: string[]) => void;
  setCurrentPageLabelInstanceIds: (ids: string[]) => void;
}

// Safe no-op default so consumers don't crash when the provider is briefly
// missing (e.g. during route transitions with `defaultPreload: 'intent'`).
export const defaultCheckedItemsState: CheckedItemsState = {
  checkedItemIds: [],
  setChecked: () => {},
  setAllChecked: () => {},
  allChecked: false,
  setMultipleChecked: () => {},
  currentPageCheckedIds: [],
  currentPageItemIds: [],
  isCurrentPagePartiallyChecked: false,
  isCurrentPageFullyChecked: false,
  currentPageChecked: false,
  setCurrentPageChecked: () => {},
  itemLimit: null,
  setItemLimit: () => {},
};

const defaultCombinedCheckedItemsContextValue: CombinedCheckedItemsContextValue = {
  imageCheckedItemsState: defaultCheckedItemsState,
  labelInstancesCheckedItemsState: defaultCheckedItemsState,
  isLoadingImageIds: false,
  isLoadingLabelInstanceIds: false,
  imageIds: undefined,
  labelInstanceIds: undefined,
  loadImageIds: () => {},
  loadLabelInstanceIds: () => {},
  setCurrentPageImageIds: () => {},
  setCurrentPageLabelInstanceIds: () => {},
};

export const CombinedCheckedItemsContext = createContext<CombinedCheckedItemsContextValue>(
  defaultCombinedCheckedItemsContextValue,
);

export const useCombinedCheckedItems = () => {
  return useContext(CombinedCheckedItemsContext) as CombinedCheckedItemsContextValue;
};

export const CombinedCheckedItemsProvider = (props: {children: ReactNode; mode: ImageOverviewMode}) => {
  const currentModel = useAppSelector(selectCurrentModel);
  const modelLabelItems = useLabelItems({from: 'current-model-or-project', model: currentModel});
  const isFinetuneMode = props.mode === 'model-finetune';
  const [imageFilters] = useImageFilters(props.mode, isFinetuneMode ? null : modelLabelItems);
  const [shouldLoadImageIds, setShouldLoadImageIds] = useState(false);
  const [shouldLoadLabelInstanceIds, setShouldLoadLabelInstanceIds] = useState(false);

  const currentPageImages = useAppSelector(selectImages);
  const currentPageImageIds = currentPageImages.map((img) => img.id);

  const [dynamicCurrentPageImageIds, setDynamicCurrentPageImageIds] = useState<string[]>([]);
  const [dynamicCurrentPageLabelInstanceIds, setDynamicCurrentPageLabelInstanceIds] = useState<string[]>([]);

  const imageIdsQuery = useImagesIds(imageFilters, shouldLoadImageIds);

  const labelInstancesIdsQuery = useLabelInstancesIds(imageFilters, shouldLoadLabelInstanceIds);

  // Initialize checked items hooks with current page support
  const imageCheckedItemsState = useCheckedItems({
    allItemsIds: imageIdsQuery.data?.imageIds,
    currentPageItemIds: dynamicCurrentPageImageIds.length > 0 ? dynamicCurrentPageImageIds : currentPageImageIds,
    mode: props.mode,
  });

  const labelInstancesCheckedItemsState = useCheckedItems({
    allItemsIds: labelInstancesIdsQuery.data?.ids,
    currentPageItemIds: dynamicCurrentPageLabelInstanceIds,
    mode: props.mode,
  });

  const setCurrentPageImageIds = useCallback((ids: string[]) => {
    setDynamicCurrentPageImageIds(ids);
  }, []);

  const setCurrentPageLabelInstanceIds = useCallback((ids: string[]) => {
    setDynamicCurrentPageLabelInstanceIds(ids);
  }, []);

  // Load IDs handlers
  const loadImageIds = () => {
    setShouldLoadImageIds(true);
  };

  const loadLabelInstanceIds = () => {
    setShouldLoadLabelInstanceIds(true);
  };

  // Route change handling
  const router = useRouter();
  const previousRoute = useRef<string | null>(null);

  useEffect(() => {
    const handleRouteChange = (currentRoute: Route) => {
      const shouldCacheBeKept =
        (currentRoute.includes(Route.archive) && previousRoute.current?.includes(Route.archive)) ||
        (currentRoute.includes(Route.modelSamples) && previousRoute.current?.includes(Route.modelSamples)) ||
        (currentRoute.includes(Route.modelAddSamples) && previousRoute.current?.includes(Route.modelAddSamples)) ||
        (currentRoute.includes(Route.modelFinetune) && previousRoute.current?.includes(Route.modelFinetune)) ||
        previousRoute.current === null;

      if (!shouldCacheBeKept) {
        imageCheckedItemsState.setAllChecked(false);
        labelInstancesCheckedItemsState.setAllChecked(false);
        setShouldLoadImageIds(false);
        setShouldLoadLabelInstanceIds(false);
        setDynamicCurrentPageImageIds([]);
        setDynamicCurrentPageLabelInstanceIds([]);
      }

      previousRoute.current = currentRoute;
    };

    const unsubscribe = router.subscribe('onBeforeNavigate', (event) =>
      handleRouteChange(event.toLocation.pathname as Route),
    );
    return () => unsubscribe();
  }, [imageCheckedItemsState, labelInstancesCheckedItemsState, router]);

  const value: CombinedCheckedItemsContextValue = {
    imageCheckedItemsState,
    labelInstancesCheckedItemsState,
    isLoadingImageIds: shouldLoadImageIds && imageIdsQuery.isLoading,
    isLoadingLabelInstanceIds: shouldLoadLabelInstanceIds && labelInstancesIdsQuery.isLoading,
    imageIds: imageIdsQuery.data?.imageIds,
    labelInstanceIds: labelInstancesIdsQuery.data?.ids,
    loadImageIds,
    loadLabelInstanceIds,
    setCurrentPageImageIds,
    setCurrentPageLabelInstanceIds,
  };

  return <CombinedCheckedItemsContext.Provider value={value}>{props.children}</CombinedCheckedItemsContext.Provider>;
};
